import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors, useDraggable, useDroppable } from '@dnd-kit/core';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import axios from 'axios';
import Navigation from '../components/Navigation';
import { mainContentClassName, useNav } from '../context/NavContext';
import { getImageUrl } from '../utils/imageUtils';
import 'react-big-calendar/lib/css/react-big-calendar.css';

// Configure moment to use Monday as the first day of the week (ISO week)
moment.locale('en', {
  week: {
    dow: 1, // Monday is the first day of the week
    doy: 4  // The week that contains Jan 4th is the first week of the year
  }
});

const localizer = momentLocalizer(moment);

const MEAL_TYPE_COLORS = {
  breakfast: '#FFB226',
  lunch: '#FF6B35',
  dinner: '#4ECDC4',
  snack: '#95E1D3'
};

const MEAL_TYPE_ORDER = { breakfast: 0, lunch: 1, dinner: 2, snack: 3 };

function MobileMealPlanList({ events, currentDate, onSelectEvent, onPreviousMonth, onNextMonth }) {
  const monthEvents = events.filter((event) => moment(event.start).isSame(currentDate, 'month'));

  const groupedByDate = monthEvents.reduce((acc, event) => {
    const dateKey = moment(event.start).format('YYYY-MM-DD');
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(event);
    return acc;
  }, {});

  const sortedDates = Object.keys(groupedByDate).sort();
  sortedDates.forEach((dateKey) => {
    groupedByDate[dateKey].sort(
      (a, b) =>
        (MEAL_TYPE_ORDER[a.resource.mealType] ?? 999) -
        (MEAL_TYPE_ORDER[b.resource.mealType] ?? 999)
    );
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-300">
        <div className="flex items-center justify-center gap-4 flex-1">
          <button
            type="button"
            onClick={onPreviousMonth}
            className="w-8 h-8 flex items-center justify-center bg-emerald-600 text-white rounded-md font-semibold hover:bg-emerald-700 transition-colors shadow-md text-lg"
            aria-label="Previous month"
          >
            ‹
          </button>
          <div className="text-lg font-bold text-gray-800">
            {moment(currentDate).format('MMMM YYYY')}
          </div>
          <button
            type="button"
            onClick={onNextMonth}
            className="w-8 h-8 flex items-center justify-center bg-emerald-600 text-white rounded-md font-semibold hover:bg-emerald-700 transition-colors shadow-md text-lg"
            aria-label="Next month"
          >
            ›
          </button>
        </div>
      </div>

      {sortedDates.length === 0 ? (
        <p className="text-gray-500 text-center py-8">No meals planned this month.</p>
      ) : (
        <div className="space-y-5">
          {sortedDates.map((dateKey) => (
            <section key={dateKey}>
              <h3 className="font-bold text-gray-800 mb-2 text-sm sm:text-base">
                {moment(dateKey).format('dddd, MMMM D')}
              </h3>
              <ul className="space-y-2">
                {groupedByDate[dateKey].map((event) => {
                  const { mealType } = event.resource;
                  const isLeftover = Boolean(event.resource?.isLeftover);
                  const isFromPantry = Boolean(event.resource?.isFreezerDinner);
                  const noCookNeeded = isLeftover || isFromPantry;
                  const backgroundColor = MEAL_TYPE_COLORS[mealType] || MEAL_TYPE_COLORS.snack;

                  return (
                    <li key={event.id}>
                      <button
                        type="button"
                        onClick={() => onSelectEvent(event)}
                        className="w-full text-left rounded-lg px-3 py-2.5 shadow-sm active:opacity-90"
                        style={{
                          backgroundColor,
                          border: noCookNeeded ? '2px dashed #374151' : 'none',
                          opacity: noCookNeeded ? 0.85 : 1
                        }}
                      >
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-800">
                          {mealType}
                        </span>
                        <p className="font-medium text-gray-900 leading-snug">{event.title}</p>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function PantryItemCard({ item, onSchedule, onDelete }) {
  const recipe = item.recipeId;
  return (
    <div className="bg-sky-50 border border-sky-200 rounded-lg shadow-sm p-3 mb-2">
      <p className="font-semibold text-sm">{recipe?.title || 'Unknown recipe'}</p>
      <p className="text-xs text-gray-600 capitalize">
        {item.mealType} · {item.portionsRemaining} portion(s)
      </p>
      <p className="text-xs text-gray-500 mb-2">
        Cooked {moment(item.cookedAt).format('MMM D, YYYY')}
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onSchedule(item)}
          className="flex-1 px-2 py-1.5 text-xs bg-sky-600 text-white rounded hover:bg-sky-700"
        >
          Schedule
        </button>
        <button
          type="button"
          onClick={() => onDelete(item._id)}
          className="px-2 py-1.5 text-xs bg-stone-500 text-white rounded hover:bg-stone-600"
        >
          Remove
        </button>
      </div>
    </div>
  );
}

function DraggableRecipe({ recipe }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ 
    id: `recipe-${recipe._id}`,
    data: { type: 'recipe', recipe }
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{ opacity: isDragging ? 0.5 : 1 }}
      className="bg-white rounded-lg shadow-md p-4 mb-2 cursor-move hover:shadow-lg transition-shadow"
    >
      <div className="flex items-center gap-3">
        {recipe.imageUrl ? (
          <img src={getImageUrl(recipe.imageUrl)} alt={recipe.title} className="w-16 h-16 object-cover rounded" />
        ) : (
          <div className="w-16 h-16 bg-orange-100 rounded flex items-center justify-center flex-shrink-0">
            <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
        <div>
          <h3 className="font-semibold">{recipe.title}</h3>
          <p className="text-sm text-gray-600">{recipe.totalTime || (recipe.prepTime && recipe.cookTime ? recipe.prepTime + recipe.cookTime : 'N/A')} min • {recipe.servings} servings</p>
        </div>
      </div>
    </div>
  );
}

function formatMealPlanTitle(mealPlan) {
  const title = mealPlan.recipeId?.title || 'Unknown Recipe';
  if (mealPlan.pantryItemId || mealPlan.leftoverSource === 'freezer') {
    return `${title} (from freezer)`;
  }
  if (mealPlan.isLeftover) {
    return `${title} (leftovers)`;
  }
  if (mealPlan.isBatchCook) {
    return `${title} (batch cook)`;
  }
  return title;
}

function getMealPlanEventFlags(mealPlan) {
  const isFromPantry = Boolean(mealPlan.pantryItemId || mealPlan.leftoverSource === 'freezer');
  const isBatchCook = Boolean(mealPlan.isBatchCook);
  const isLeftover = Boolean(mealPlan.isLeftover || isFromPantry);
  return { isLeftover, isFromPantry, isFreezerDinner: isFromPantry, isBatchCook };
}

function DraggableEvent({ event, onSelectEvent }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `event-${event.id}`,
    data: { type: 'event', event }
  });
  const touchStartRef = useRef(null);
  const touchStartTimeRef = useRef(null);
  const isLeftover = Boolean(event.resource?.isLeftover);
  const isFreezerDinner = Boolean(event.resource?.isFreezerDinner);
  const isBatchCook = Boolean(event.resource?.isBatchCook);
  const noCookNeeded = isLeftover || isFreezerDinner;
  const eventPrefix = isFreezerDinner ? '❄ ' : isLeftover ? '↻ ' : isBatchCook ? '◆ ' : '';

  const handleTouchStart = (e) => {
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    touchStartTimeRef.current = Date.now();
  };

  const handleTouchEnd = (e) => {
    if (!touchStartRef.current || !touchStartTimeRef.current) return;
    
    const touchEnd = { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY };
    const distance = Math.sqrt(
      Math.pow(touchEnd.x - touchStartRef.current.x, 2) + 
      Math.pow(touchEnd.y - touchStartRef.current.y, 2)
    );
    const duration = Date.now() - touchStartTimeRef.current;
    
    // If it's a quick tap (not a drag), trigger click
    if (distance < 10 && duration < 300 && !isDragging) {
      e.preventDefault();
      e.stopPropagation();
      if (onSelectEvent) {
        onSelectEvent(event);
      }
    }
    
    touchStartRef.current = null;
    touchStartTimeRef.current = null;
  };

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onClick={(e) => {
        // Prevent event click if dragging
        if (isDragging) {
          e.stopPropagation();
          return;
        }
        // Allow click on desktop
        if (onSelectEvent && !isDragging) {
          onSelectEvent(event);
        }
      }}
      style={{
        opacity: isDragging ? 0.5 : noCookNeeded ? 0.92 : 1,
        cursor: 'move',
        height: '100%',
        width: '100%',
        padding: '2px 5px',
        boxSizing: 'border-box',
        border: noCookNeeded ? '2px dashed #1f2937' : '2px solid transparent',
        borderRadius: noCookNeeded ? '4px' : '2px'
      }}
      className={noCookNeeded ? 'rbc-event-content font-medium' : 'rbc-event-content'}
      title={event.title}
    >
      {eventPrefix}{event.title}
    </div>
  );
}

// Create stable no-op handlers outside component to ensure they're always available
const NO_OP_DRAG_START = (e) => {
  if (e) {
    if (e.preventDefault) e.preventDefault();
    if (e.stopPropagation) e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();
  }
};

const NO_OP_DRAG_END = () => {};

function EventWrapper({ event, ...props }) {
  const wrapperRef = useRef(null);
  const handlersRef = useRef({});

  // CRITICAL: Ensure handleDragStart/handleDragEnd are ALWAYS functions IN PROPS
  // React-big-calendar's internal code accesses these from props/closure
  // We must ensure they exist in props before react-big-calendar tries to use them
  if (!props.handleDragStart || typeof props.handleDragStart !== 'function') {
    props.handleDragStart = NO_OP_DRAG_START;
  }
  if (!props.handleDragEnd || typeof props.handleDragEnd !== 'function') {
    props.handleDragEnd = NO_OP_DRAG_END;
  }
  
  // Now extract from props - they're guaranteed to be functions
  const handleDragStart = props.handleDragStart;
  const handleDragEnd = props.handleDragEnd;
  const safeProvidedHandleDragStart = handleDragStart;
  const safeProvidedHandleDragEnd = handleDragEnd;

  // Extract all non-DOM props that react-big-calendar passes
  const {
    onDragStart,
    onDragEnd,
    draggable,
    continuesPrior,
    continuesAfter,
    slotStart,
    slotEnd,
    ...domProps
  } = props;

  // Always provide function handlers - ensure they're always functions
  // Use ref to store handlers so they're stable across renders
  if (!handlersRef.current.handleDragStart) {
    handlersRef.current.handleDragStart = safeProvidedHandleDragStart;
  } else {
    handlersRef.current.handleDragStart = safeProvidedHandleDragStart;
  }
  
  if (!handlersRef.current.handleDragEnd) {
    handlersRef.current.handleDragEnd = safeProvidedHandleDragEnd;
  } else {
    handlersRef.current.handleDragEnd = safeProvidedHandleDragEnd;
  }
  
  if (!handlersRef.current.onDragStart) {
    handlersRef.current.onDragStart = typeof onDragStart === 'function' ? onDragStart : NO_OP_DRAG_START;
  } else if (typeof onDragStart === 'function') {
    handlersRef.current.onDragStart = onDragStart;
  }
  
  if (!handlersRef.current.onDragEnd) {
    handlersRef.current.onDragEnd = typeof onDragEnd === 'function' ? onDragEnd : NO_OP_DRAG_END;
  } else if (typeof onDragEnd === 'function') {
    handlersRef.current.onDragEnd = onDragEnd;
  }

  const safeHandleDragStart = handlersRef.current.handleDragStart;
  const safeHandleDragEnd = handlersRef.current.handleDragEnd;
  const safeOnDragStart = handlersRef.current.onDragStart;
  const safeOnDragEnd = handlersRef.current.onDragEnd;

  // Use layout effect to attach handlers synchronously after every render
  // Include all handler dependencies to ensure they're always up to date
  useLayoutEffect(() => {
    const el = wrapperRef.current;
    if (el) {
      // Attach handlers to DOM element - react-big-calendar looks for these
      // CRITICAL: These must be actual functions, not undefined
      el.handleDragStart = safeHandleDragStart || NO_OP_DRAG_START;
      el.handleDragEnd = safeHandleDragEnd || NO_OP_DRAG_END;
      el.onDragStart = safeOnDragStart || NO_OP_DRAG_START;
      el.onDragEnd = safeOnDragEnd || NO_OP_DRAG_END;
      
      // Double-check they're functions - react-big-calendar expects functions
      if (typeof el.handleDragStart !== 'function') {
        el.handleDragStart = NO_OP_DRAG_START;
      }
      if (typeof el.handleDragEnd !== 'function') {
        el.handleDragEnd = NO_OP_DRAG_END;
      }
      if (typeof el.onDragStart !== 'function') {
        el.onDragStart = NO_OP_DRAG_START;
      }
      if (typeof el.onDragEnd !== 'function') {
        el.onDragEnd = NO_OP_DRAG_END;
      }
    }
  }, [safeHandleDragStart, safeHandleDragEnd, safeOnDragStart, safeOnDragEnd]);

  // Create wrapper props
  // CRITICAL: We must provide handleDragStart/handleDragEnd as properties
  // React-big-calendar accesses them from its closure/context
  // We'll create a wrapper element that stores these but doesn't pass them to DOM
  const wrapperProps = {
    ...domProps,
    // Store handleDragStart/handleDragEnd on the wrapper object
    // React-big-calendar might access these from props or closure
    handleDragStart: safeHandleDragStart,
    handleDragEnd: safeHandleDragEnd,
    // Provide onDragStart that react-big-calendar can call
    // React-big-calendar's internal code calls handleDragStart from within onDragStart
    onDragStart: (e) => {
      // First, ensure handleDragStart exists on the element if it doesn't
      const el = e.currentTarget || e.target;
      if (el && typeof el.handleDragStart !== 'function') {
        el.handleDragStart = safeHandleDragStart || NO_OP_DRAG_START;
      }
      
      // Call handleDragStart - react-big-calendar expects this in closure
      const handler = safeHandleDragStart || NO_OP_DRAG_START;
      if (typeof handler === 'function') {
        handler(e);
      }
      
      // Always prevent default to stop native drag
      if (e) {
        if (e.preventDefault) e.preventDefault();
        if (e.stopPropagation) e.stopPropagation();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      }
    },
    onDragEnd: (e) => {
      // Try to get handleDragEnd from the element itself first
      const el = e.currentTarget || e.target;
      if (el && typeof el.handleDragEnd === 'function') {
        el.handleDragEnd(e);
      } else if (safeOnDragEnd) {
        safeOnDragEnd(e);
      }
    },
    draggable: false,
    style: { ...domProps.style, cursor: 'move' },
    onMouseDown: (e) => {
      // Prevent react-big-calendar's drag from starting
      e.stopPropagation();
      if (domProps.onMouseDown) domProps.onMouseDown(e);
    },
    ref: (el) => {
      wrapperRef.current = el;
      
      // CRITICAL: Attach handlers IMMEDIATELY and SYNCHRONOUSLY
      // This MUST happen before any other code runs
      if (el) {
        // Attach handlers directly - no checks, just assign immediately
        el.handleDragStart = safeHandleDragStart;
        el.handleDragEnd = safeHandleDragEnd;
        el.onDragStart = safeOnDragStart;
        el.onDragEnd = safeOnDragEnd;
        
        // Ensure they can't be undefined
        if (!el.handleDragStart) el.handleDragStart = NO_OP_DRAG_START;
        if (!el.handleDragEnd) el.handleDragEnd = NO_OP_DRAG_END;
        if (!el.onDragStart) el.onDragStart = NO_OP_DRAG_START;
        if (!el.onDragEnd) el.onDragEnd = NO_OP_DRAG_END;
      }
      
      // Also handle the original ref if it exists
      if (domProps.ref) {
        if (typeof domProps.ref === 'function') {
          domProps.ref(el);
        } else {
          domProps.ref.current = el;
        }
      }
    }
  };

  // CRITICAL: We need to keep handleDragStart/handleDragEnd available in props object
  // React-big-calendar's internal code accesses them from props closure
  // But we must filter them out before passing to DOM element
  // Use a wrapper div that preserves the props closure but filters for DOM
  const finalDomProps = { ...wrapperProps };
  
  // Remove non-DOM props before passing to div
  delete finalDomProps.handleDragStart;
  delete finalDomProps.handleDragEnd;
  delete finalDomProps.continuesPrior;
  delete finalDomProps.continuesAfter;
  delete finalDomProps.slotStart;
  delete finalDomProps.slotEnd;
  
  return (
    <div {...finalDomProps}>
      {props.children}
    </div>
  );
}

function Calendar() {
  const navigate = useNavigate();
  const { isCollapsed } = useNav();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [recipes, setRecipes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSeason, setFilterSeason] = useState('');
  const [showUnplannedOnly, setShowUnplannedOnly] = useState(false);
  const [dragActiveId, setDragActiveId] = useState(null);
  const [dragActiveData, setDragActiveData] = useState(null);
  const [hoveredDate, setHoveredDate] = useState(null);
  const [showMealTypeModal, setShowMealTypeModal] = useState(false);
  const [pendingRecipe, setPendingRecipe] = useState(null);
  const [pendingDate, setPendingDate] = useState(null);
  const mouseMoveHandlerRef = useRef(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const recipesContainerRef = useRef(null);
  const calendarContainerRef = useRef(null);
  const [calendarHeight, setCalendarHeight] = useState(null);
  const [showEmptyCalendarModal, setShowEmptyCalendarModal] = useState(false);
  const [emptyStartDate, setEmptyStartDate] = useState('');
  const [emptyEndDate, setEmptyEndDate] = useState('');
  const [pantryItems, setPantryItems] = useState([]);
  const [pendingPantryItem, setPendingPantryItem] = useState(null);
  const [showPantryScheduleModal, setShowPantryScheduleModal] = useState(false);
  const [pantryScheduleDate, setPantryScheduleDate] = useState('');
  const [leftoverDeletePrompt, setLeftoverDeletePrompt] = useState(null);
  const [isMobileView, setIsMobileView] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 767
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    fetchMealPlans();
    fetchRecipes();
  }, []);

  useEffect(() => {
    const updateMobileView = () => setIsMobileView(window.innerWidth <= 767);
    window.addEventListener('resize', updateMobileView);
    return () => window.removeEventListener('resize', updateMobileView);
  }, []);

  const fetchPantry = async () => {
    try {
      const response = await axios.get(`/api/pantry`);
      setPantryItems(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching pantry:', error);
      setPantryItems([]);
    }
  };

  // Safety: Ensure scroll is restored when component unmounts or drag ends
  useEffect(() => {
    return () => {
      // Cleanup on unmount - ensure scroll is restored
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, []);

  // Safety: Restore scroll when dragActiveId becomes null
  useEffect(() => {
    if (!dragActiveId) {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }
  }, [dragActiveId]);

  // Fix background height on mobile to prevent white space
  useEffect(() => {
    const updateBackgroundHeight = () => {
      if (window.innerWidth <= 767) {
        const bgElement = document.getElementById('background-fixed');
        if (bgElement) {
          // Set height to match document scroll height
          const docHeight = Math.max(
            document.body.scrollHeight,
            document.body.offsetHeight,
            document.documentElement.clientHeight,
            document.documentElement.scrollHeight,
            document.documentElement.offsetHeight
          );
          bgElement.style.height = `${docHeight}px`;
        }
      }
    };

    // Update on mount and resize
    updateBackgroundHeight();
    window.addEventListener('resize', updateBackgroundHeight);
    window.addEventListener('scroll', updateBackgroundHeight);
    
    // Also update after a short delay to account for content loading
    const timeoutId = setTimeout(updateBackgroundHeight, 100);

    return () => {
      window.removeEventListener('resize', updateBackgroundHeight);
      window.removeEventListener('scroll', updateBackgroundHeight);
      clearTimeout(timeoutId);
    };
  }, [events, recipes]); // Re-run when content changes

  // Measure and match calendar height to recipes container (desktop grid only)
  useEffect(() => {
    if (isMobileView) {
      setCalendarHeight(null);
      return undefined;
    }

    const updateCalendarHeight = () => {
      if (recipesContainerRef.current && calendarContainerRef.current) {
        const recipesHeight = recipesContainerRef.current.offsetHeight;
        setCalendarHeight(recipesHeight);
      }
    };

    updateCalendarHeight();
    window.addEventListener('resize', updateCalendarHeight);
    const timeoutId = setTimeout(updateCalendarHeight, 100);
    const navTransitionId = setTimeout(updateCalendarHeight, 350);

    return () => {
      window.removeEventListener('resize', updateCalendarHeight);
      clearTimeout(timeoutId);
      clearTimeout(navTransitionId);
    };
  }, [recipes, searchTerm, isCollapsed, isMobileView, events]);

  const fetchMealPlans = async () => {
    try {
      const [mealPlansResponse] = await Promise.all([
        axios.get(`/api/meal-plans`),
        fetchPantry()
      ]);
      const response = mealPlansResponse;
      
      // Define meal type order
      const mealTypeOrder = { breakfast: 0, lunch: 1, dinner: 2, snack: 3 };
      
      const formattedEvents = response.data
        .filter(mealPlan => mealPlan.recipeId) // Filter out meal plans with missing recipes
        .map(mealPlan => {
          const flags = getMealPlanEventFlags(mealPlan);
          return {
        id: mealPlan._id,
          title: formatMealPlanTitle(mealPlan),
        start: new Date(mealPlan.date),
        end: new Date(moment(mealPlan.date).add(1, 'hour')),
        resource: {
            recipeId: mealPlan.recipeId?._id,
          mealType: mealPlan.mealType,
          recipe: mealPlan.recipeId,
          ...flags
        }
        };
        })
        .sort((a, b) => {
          // First sort by date
          const dateDiff = a.start.getTime() - b.start.getTime();
          if (dateDiff !== 0) return dateDiff;
          
          // If same date, sort by meal type order
          const aOrder = mealTypeOrder[a.resource.mealType] ?? 999;
          const bOrder = mealTypeOrder[b.resource.mealType] ?? 999;
          return aOrder - bOrder;
        });
      setEvents(formattedEvents);
    } catch (error) {
      console.error('Error fetching meal plans:', error);
      console.error('Error details:', error.response?.data);
    }
  };


  const fetchRecipes = async () => {
    try {
      const response = await axios.get(`/api/recipes`);
      setRecipes(response.data);
    } catch (error) {
      console.error('Error fetching recipes:', error);
    }
  };

  const handleSelectEvent = (event) => {
    // Don't open modal if we're currently dragging
    if (dragActiveId) {
      return;
    }
    setSelectedEvent(event);
    setShowModal(true);
  };

  const handleSelectSlot = ({ start }) => {
    // Don't handle slot selection if we're dragging (handled by dragEnd)
    if (dragActiveId) {
      return;
    }
  };

  const navigatePeriod = (direction) => {
    setCurrentDate(moment(currentDate).add(direction, 'months').toDate());
  };

  const handleNavigate = (date) => {
    setCurrentDate(date);
  };


  // Custom Toolbar Component
  const CustomToolbar = ({ label, onNavigate, onView, view }) => {
    return (
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-300">
        <div className="flex items-center justify-center gap-4 flex-1">
          <button
            onClick={() => navigatePeriod(-1)}
            className="w-8 h-8 flex items-center justify-center bg-emerald-600 text-white rounded-md font-semibold hover:bg-emerald-700 transition-colors shadow-md text-lg"
            aria-label="Previous month"
          >
            ‹
          </button>
          <div className="text-lg font-bold text-gray-800">
            {label}
          </div>
          <button
            onClick={() => navigatePeriod(1)}
            className="w-8 h-8 flex items-center justify-center bg-emerald-600 text-white rounded-md font-semibold hover:bg-emerald-700 transition-colors shadow-md text-lg"
            aria-label="Next month"
          >
            ›
          </button>
        </div>
      </div>
    );
  };

  const handleDragStart = (event) => {
    setDragActiveId(event.active.id);
    setDragActiveData(event.active.data.current);
    
    // Prevent body scroll during drag on mobile (only during active drag)
    // This will be restored in handleDragEnd or by useEffect when dragActiveId becomes null
    if (document.body) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    }
    
    // Add mousemove listener to track cursor position during drag
    const handleMouseMove = (e) => {
      // Find the element under the cursor
      const element = document.elementFromPoint(e.clientX, e.clientY);
      if (element) {
        // Find calendar day element
        const dayElement = element.closest('[data-date]');
        if (dayElement) {
          const dateAttr = dayElement.getAttribute('data-date');
          if (dateAttr && !dayElement.classList.contains('rbc-event') && !dayElement.classList.contains('rbc-event-content')) {
            setHoveredDate(dateAttr);
            return;
          }
        }
        
        // Also check for react-big-calendar day cells
        const dayBg = element.closest('.rbc-day-bg') || element.closest('.rbc-date-cell');
        if (dayBg) {
          const dateAttr = dayBg.getAttribute('data-date');
          if (dateAttr) {
            setHoveredDate(dateAttr);
            return;
          }
        }
      }
      // Don't clear hoveredDate here - let it persist until we find a new one
    };
    
    mouseMoveHandlerRef.current = handleMouseMove;
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('touchmove', handleMouseMove, { passive: false });
  };

  const handleDragOver = (event) => {
    // PRIMARY: Use cursor position to determine drop target
    const mouseEvent = event.dragEvent?.nativeEvent;
    if (mouseEvent) {
      let element = null;
      try {
        // Get element at exact cursor position
        element = document.elementFromPoint(mouseEvent.clientX, mouseEvent.clientY);
      } catch (e) {
        console.error('Error getting element from point:', e);
      }
      
      if (element) {
        // Walk up the DOM tree to find calendar day elements
        // We need to check multiple potential elements since the calendar structure is complex
        let currentElement = element;
        let attempts = 0;
        const maxAttempts = 10;
        
        while (currentElement && attempts < maxAttempts) {
          // Check if this element or any ancestor has data-date attribute
          const dayElement = currentElement.closest('[data-date]');
          if (dayElement) {
            const dateAttr = dayElement.getAttribute('data-date');
            if (dateAttr && !dayElement.classList.contains('rbc-event') && !dayElement.classList.contains('rbc-event-content')) {
              setHoveredDate(dateAttr);
              return;
            }
          }
          
          // Also check for rbc-day-bg or rbc-date-cell elements and extract date from their classes or data
          if (currentElement.classList) {
            // Check if we're in a calendar day cell
            const dayBg = currentElement.closest('.rbc-day-bg') || currentElement.closest('.rbc-date-cell');
            if (dayBg) {
              const dateAttr = dayBg.getAttribute('data-date');
              if (dateAttr) {
                setHoveredDate(dateAttr);
                return;
              }
            }
          }
          
          currentElement = currentElement.parentElement;
          attempts++;
        }
      }
    }
    
    // FALLBACK: Use the over property from dnd-kit if cursor detection didn't work
    const { over } = event;
    if (over && over.id && typeof over.id === 'string' && over.id.startsWith('day-')) {
      const dateStr = over.id.replace('day-', '');
      setHoveredDate(dateStr);
      return;
    }
    
    // If we couldn't find a date element, clear hovered date
    setHoveredDate(null);
  };

  const handleDragEnd = async (event) => {
    const { active } = event;
    
    // PRIMARY: Use cursor position to determine drop target
    let dropDate = null;
    const mouseEvent = event.dragEvent?.nativeEvent;
    
    if (mouseEvent) {
      let element = null;
      try {
        element = document.elementFromPoint(mouseEvent.clientX, mouseEvent.clientY);
      } catch (e) {
        console.error('Error getting element from point:', e);
      }
      
      if (element) {
        // Walk up the DOM tree to find calendar day elements
        let currentElement = element;
        let attempts = 0;
        const maxAttempts = 10;
        
        while (currentElement && attempts < maxAttempts) {
          // Check if this element or any ancestor has data-date attribute
          const dayElement = currentElement.closest('[data-date]');
          if (dayElement) {
            const dateAttr = dayElement.getAttribute('data-date');
            if (dateAttr && !dayElement.classList.contains('rbc-event') && !dayElement.classList.contains('rbc-event-content')) {
              dropDate = dateAttr;
              break;
            }
          }
          
          // Also check for rbc-day-bg or rbc-date-cell elements
          if (currentElement.classList) {
            const dayBg = currentElement.closest('.rbc-day-bg') || currentElement.closest('.rbc-date-cell');
            if (dayBg) {
              const dateAttr = dayBg.getAttribute('data-date');
              if (dateAttr) {
                dropDate = dateAttr;
                break;
              }
            }
          }
          
          currentElement = currentElement.parentElement;
          attempts++;
        }
      }
    }
    
    // FALLBACK 1: Use hoveredDate (set by handleDragOver based on cursor)
    if (!dropDate && hoveredDate) {
      dropDate = hoveredDate;
    }
    
    // FALLBACK 2: Use the over property from dnd-kit
    if (!dropDate) {
      const { over } = event;
      if (over && over.id && typeof over.id === 'string' && over.id.startsWith('day-')) {
        dropDate = over.id.replace('day-', '');
      }
    }
    
    // Always restore body scroll first (before any early returns)
    if (document.body) {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }
    
    if (!dropDate || !dragActiveId || !dragActiveData) {
      // Clean up mousemove listener
      if (mouseMoveHandlerRef.current) {
        window.removeEventListener('mousemove', mouseMoveHandlerRef.current);
        window.removeEventListener('touchmove', mouseMoveHandlerRef.current);
        mouseMoveHandlerRef.current = null;
      }
      
    setDragActiveId(null);
      setDragActiveData(null);
      setHoveredDate(null);
      return;
    }

    // Clean up mousemove listener
    if (mouseMoveHandlerRef.current) {
      window.removeEventListener('mousemove', mouseMoveHandlerRef.current);
      window.removeEventListener('touchmove', mouseMoveHandlerRef.current);
      mouseMoveHandlerRef.current = null;
    }

    // Check if dragging an event (meal plan) or a recipe
    if (dragActiveData.type === 'event') {
      // Moving an existing meal plan event
      const eventId = dragActiveData.event.id;
      try {
        // Convert date string to ISO format for proper parsing
        const dateToSend = new Date(dropDate).toISOString();
        await axios.put(`/api/meal-plans/${eventId}`, {
          date: dateToSend
        });
        await fetchMealPlans();
      } catch (error) {
        console.error('Error updating meal plan:', error);
        console.error('Error response:', error.response?.data);
        alert(`Failed to move meal plan: ${error.response?.data?.error || error.message}`);
      }
    } else if (dragActiveData.type === 'recipe') {
      // Adding a new recipe to the calendar
      const recipe = dragActiveData.recipe;
      if (recipe) {
        setPendingRecipe(recipe);
        setPendingDate(dropDate);
        setShowMealTypeModal(true);
      }
    }
    
    // Clean up mousemove listener
    if (mouseMoveHandlerRef.current) {
      window.removeEventListener('mousemove', mouseMoveHandlerRef.current);
      window.removeEventListener('touchmove', mouseMoveHandlerRef.current);
      mouseMoveHandlerRef.current = null;
    }
    
    // Restore body scroll (already restored above, but ensure it's done)
    if (document.body) {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }
    
    setDragActiveId(null);
    setDragActiveData(null);
    setHoveredDate(null);
  };

  const handleMealTypeSelect = async (mealType) => {
    if (!pendingDate) return;
    if (!pendingRecipe && !pendingPantryItem) return;

    try {
      const dateToSend = new Date(pendingDate).toISOString();
      const payload = {
        date: dateToSend,
        mealType
      };

      if (pendingPantryItem) {
        payload.recipeId = pendingPantryItem.recipeId?._id || pendingPantryItem.recipeId;
        payload.pantryItemId = pendingPantryItem._id;
      } else {
        payload.recipeId = pendingRecipe._id;
      }

      const response = await axios.post(`/api/meal-plans`, payload);
      
      console.log('Meal plan saved successfully:', response.data);
      
      setShowMealTypeModal(false);
      setPendingRecipe(null);
      setPendingPantryItem(null);
      setPendingDate(null);
      
      await fetchMealPlans();
      await fetchPantry();
      alert('Meal plan saved successfully!');
    } catch (error) {
      console.error('Error saving meal plan:', error);
      console.error('Error response:', error.response?.data);
      alert(`Failed to save meal plan: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleSchedulePantryItem = (item) => {
    setPendingPantryItem(item);
    setPendingRecipe(item.recipeId);
    setPantryScheduleDate(moment().format('YYYY-MM-DD'));
    setShowPantryScheduleModal(true);
  };

  const confirmPantrySchedule = async () => {
    if (!pendingPantryItem || !pantryScheduleDate) return;
    try {
      await axios.post(`/api/meal-plans`, {
        date: new Date(pantryScheduleDate).toISOString(),
        recipeId: pendingPantryItem.recipeId?._id || pendingPantryItem.recipeId,
        mealType: pendingPantryItem.mealType,
        pantryItemId: pendingPantryItem._id
      });
      setShowPantryScheduleModal(false);
      setPendingPantryItem(null);
      setPendingRecipe(null);
      await fetchMealPlans();
      await fetchPantry();
      alert('Meal scheduled from pantry!');
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to schedule from pantry');
    }
  };

  const handleDeletePantryItem = async (pantryItemId) => {
    if (
      !window.confirm(
        'Discard these freezer portions for this batch? They will not come back unless you cook this recipe again.'
      )
    ) {
      return;
    }
    try {
      await axios.delete(`/api/pantry/${pantryItemId}`);
      await fetchPantry();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to remove pantry item');
    }
  };

  const completeMealPlanDelete = async (mealPlanId, alsoDeleteLeftovers) => {
    const params = alsoDeleteLeftovers === undefined
      ? {}
      : { alsoDeleteLeftovers: String(alsoDeleteLeftovers) };
    await axios.delete(`/api/meal-plans/${mealPlanId}`, { params });
    await fetchMealPlans();
    await fetchPantry();
    setShowModal(false);
    setSelectedEvent(null);
    setLeftoverDeletePrompt(null);
  };

  const handleDeleteMealPlan = async () => {
    if (!selectedEvent || !window.confirm('Are you sure you want to delete this meal?')) {
      return;
    }
    try {
      await completeMealPlanDelete(selectedEvent.id);
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.requiresLeftoverChoice) {
        setLeftoverDeletePrompt({
          mealPlanId: selectedEvent.id,
          leftoverMeals: error.response.data.leftoverMeals || []
        });
        return;
      }
      console.error('Error deleting meal plan:', error);
      alert(error.response?.data?.error || 'Failed to delete meal plan');
    }
  };

  const handleLeftoverDeleteChoice = async (alsoDeleteLeftovers) => {
    if (!leftoverDeletePrompt) {
      return;
    }
    try {
      await completeMealPlanDelete(leftoverDeletePrompt.mealPlanId, alsoDeleteLeftovers);
    } catch (error) {
      console.error('Error deleting meal plan:', error);
      alert(error.response?.data?.error || 'Failed to delete meal plan');
    }
  };

  const handleEmptyCalendar = async () => {
    if (!emptyStartDate || !emptyEndDate) {
      alert('Please select both start and end dates');
      return;
    }
    
    if (new Date(emptyStartDate) > new Date(emptyEndDate)) {
      alert('Start date must be before or equal to end date');
      return;
    }
    
    const confirmMessage = `Are you sure you want to delete all meal plans from ${moment(emptyStartDate).format('MMMM D, YYYY')} to ${moment(emptyEndDate).format('MMMM D, YYYY')}? This action cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      const response = await axios.delete(`/api/meal-plans/range`, {
        data: {
          startDate: emptyStartDate,
          endDate: emptyEndDate
        }
      });
      
      alert(`Successfully deleted ${response.data.deletedCount} meal plan(s)`);
      setShowEmptyCalendarModal(false);
      setEmptyStartDate('');
      setEmptyEndDate('');
      fetchMealPlans();
      fetchPantry();
    } catch (error) {
      console.error('Error emptying calendar:', error);
      alert(`Failed to empty calendar: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleMealTypeChange = async (newMealType) => {
    if (!selectedEvent) return;
    
    try {
      await axios.put(`/api/meal-plans/${selectedEvent.id}`, {
        mealType: newMealType
      });
      fetchMealPlans();
      // Update the selected event to reflect the change
      setSelectedEvent({
        ...selectedEvent,
        resource: {
          ...selectedEvent.resource,
          mealType: newMealType
        }
      });
    } catch (error) {
      console.error('Error updating meal type:', error);
      alert('Failed to update meal type');
    }
  };

  const eventStyleGetter = (event) => {
    const isLeftover = event.resource?.isLeftover;
    const isFreezerDinner = event.resource?.isFreezerDinner;
    const noCookNeeded = isLeftover || isFreezerDinner;

    return {
      style: {
        backgroundColor: MEAL_TYPE_COLORS[event.resource.mealType] || MEAL_TYPE_COLORS.snack,
        border: noCookNeeded ? '2px dashed #374151' : 'none',
        color: '#000',
        opacity: noCookNeeded ? 0.85 : 1
      }
    };
  };

  // Get list of recipe IDs that are already planned
  const plannedRecipeIds = new Set(
    events
      .map(event => event.resource?.recipeId || event.resource?.recipe?._id)
      .filter(id => id) // Remove any undefined/null values
  );

  const filteredRecipes = recipes.filter(recipe => {
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      if (!recipe.title.toLowerCase().includes(searchLower)) {
        return false;
      }
    }
    
    // Season filter
    if (filterSeason) {
      const recipeSeasons = recipe.seasons || [];
      // If recipe has no seasons specified, it's available year-round (show it)
      // If recipe has seasons, it must include the selected season
      if (recipeSeasons.length > 0 && !recipeSeasons.includes(filterSeason)) {
        return false;
      }
    }
    
    // Unplanned only filter
    if (showUnplannedOnly) {
      if (plannedRecipeIds.has(recipe._id)) {
        return false;
      }
    }
    
    return true;
  });

  const draggedItem = dragActiveData?.type === 'recipe' 
    ? dragActiveData.recipe 
    : dragActiveData?.type === 'event'
    ? dragActiveData.event
    : null;

  // Custom day component with hover feedback and droppable
  const DroppableDay = ({ date, children, className = '' }) => {
    const dateStr = moment(date).format('YYYY-MM-DD');
    const { setNodeRef } = useDroppable({
      id: `day-${dateStr}`,
      data: { date: dateStr }
    });
    
    // Only use hoveredDate for highlighting (set based on cursor position)
    // Don't use isOver from dnd-kit as it tracks where the drag overlay is, not where the cursor is
    const isHovered = hoveredDate === dateStr && dragActiveId !== null;
    
    return (
      <div
        ref={setNodeRef}
        data-date={dateStr}
        className={className}
        style={{
          backgroundColor: isHovered ? '#fed7aa' : undefined,
          borderRadius: isHovered ? '8px' : undefined,
          boxShadow: isHovered ? '0 4px 6px rgba(0, 0, 0, 0.1)' : undefined,
          transition: 'all 0.2s ease',
          minHeight: '100%',
          width: '100%',
          position: 'relative',
          zIndex: isHovered ? 1 : 0
        }}
      >
        {children}
      </div>
    );
  };

  // Custom day component wrapper for month view
  const DayComponent = ({ value, children }) => {
    const dateStr = moment(value).format('YYYY-MM-DD');
    
    // Wrap the day cell with droppable zone
  return (
      <DroppableDay date={value} className="">
        {children}
      </DroppableDay>
    );
  };

  return (
    <>
      <div 
        id="background-fixed"
        className="fixed inset-0"
        style={{
          backgroundImage: 'url(/img/plan-background.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
          zIndex: -1
        }}
      />
      <style>{`
        @media (max-width: 767px) {
          #background-fixed {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            width: 100vw;
            min-height: 100vh;
            background-attachment: fixed;
            background-size: cover;
            background-position: center;
            background-repeat: no-repeat;
          }
        }
      `}</style>
      <Navigation />
      <div className={mainContentClassName}>
      <DndContext 
        sensors={sensors} 
        onDragStart={handleDragStart} 
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className={`mx-auto w-full transition-[max-width] duration-300 ease-in-out ${isCollapsed ? 'max-w-none' : 'max-w-6xl'}`}>
          <div className="flex justify-between items-center mb-4 sm:mb-8 flex-wrap gap-4">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold">Meal Planning Calendar</h1>
            <div className="flex gap-2 sm:gap-3 flex-wrap">
              <button
                onClick={() => setShowEmptyCalendarModal(true)}
                className="px-4 sm:px-6 py-2 sm:py-3 bg-rose-600 text-white rounded-lg font-semibold hover:bg-rose-700 transition-colors shadow-md text-sm sm:text-base whitespace-nowrap"
              >
                Empty Calendar
              </button>
              <button
                onClick={() => navigate('/generate-meal-plan')}
                className="px-4 sm:px-6 py-2 sm:py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors shadow-md text-sm sm:text-base whitespace-nowrap"
              >
                Generate Meal Plan
              </button>
            </div>
          </div>
          
          <div className={`grid grid-cols-1 gap-4 sm:gap-6 items-start ${isCollapsed ? 'lg:grid-cols-[36rem_1fr]' : 'lg:grid-cols-2'}`}>
            {/* Left Side - Recipes List */}
            <div ref={recipesContainerRef} className={`bg-white/60 backdrop-blur-sm rounded-lg shadow-lg p-4 sm:p-6 order-2 lg:order-1 ${isCollapsed ? 'lg:max-w-[36rem]' : ''}`}>
              <h2 className="text-xl sm:text-2xl font-bold mb-4">Available Recipes</h2>

              <div className="mb-6 border-b border-gray-200 pb-4">
                <h3 className="font-semibold text-gray-800 mb-2">Freezer / pantry</h3>
                {pantryItems.length > 0 ? (
                  <>
                    <p className="text-xs text-gray-500 mb-3">
                      Schedule saved portions when you are ready to eat them.
                    </p>
                    <div className="max-h-48 overflow-y-auto">
                      {pantryItems.map((item) => (
                        <PantryItemCard
                          key={item._id}
                          item={item}
                          onSchedule={handleSchedulePantryItem}
                          onDelete={handleDeletePantryItem}
                        />
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="text-xs text-gray-500">
                    No unscheduled portions in your freezer. Batch-cook dinners add portions here after you save a meal plan. Portions already scheduled on the calendar are not listed.
                  </p>
                )}
              </div>
              
              <input
                type="text"
                placeholder="Search recipes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full p-2 sm:p-3 border border-gray-300 rounded mb-4 text-sm sm:text-base"
              />
              
              <select
                value={filterSeason}
                onChange={(e) => setFilterSeason(e.target.value)}
                className="w-full p-2 sm:p-3 border border-gray-300 rounded mb-4 text-sm sm:text-base bg-white"
              >
                <option value="">All Seasons</option>
                <option value="spring">Spring</option>
                <option value="summer">Summer</option>
                <option value="fall">Fall</option>
                <option value="winter">Winter</option>
              </select>
              
              <label className="flex items-center gap-2 mb-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showUnplannedOnly}
                  onChange={(e) => setShowUnplannedOnly(e.target.checked)}
                  className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                />
                <span className="text-sm sm:text-base text-gray-700">
                  Show only unplanned recipes
                </span>
              </label>

              <div className="space-y-2 max-h-[400px] sm:max-h-[600px] overflow-y-auto">
                {filteredRecipes.map(recipe => (
                  <DraggableRecipe
                    key={recipe._id}
                    recipe={recipe}
                  />
                ))}
              </div>
            </div>

            {/* Right Side - Calendar View */}
            <div
              ref={calendarContainerRef}
              className="bg-white/60 backdrop-blur-sm rounded-lg shadow-lg p-3 sm:p-4 order-1 lg:order-2 min-w-0"
              style={{
                position: 'relative',
                height: !isMobileView && calendarHeight ? `${calendarHeight}px` : 'auto'
              }}
            >
              {isMobileView ? (
                <MobileMealPlanList
                  events={events}
                  currentDate={currentDate}
                  onSelectEvent={handleSelectEvent}
                  onPreviousMonth={() => navigatePeriod(-1)}
                  onNextMonth={() => navigatePeriod(1)}
                />
              ) : (
                <>
              <style>{`
                .rbc-day-bg[data-date] {
                  transition: background-color 0.2s ease, box-shadow 0.2s ease;
                }
                .rbc-time-slot[data-date] {
                  transition: background-color 0.2s ease, box-shadow 0.2s ease;
                }
                /* Completely disable react-big-calendar's native drag */
                .rbc-event {
                  pointer-events: none;
                  user-select: none;
                }
                .rbc-event-content {
                  pointer-events: auto;
                  user-select: none;
                  touch-action: pan-y;
                }
                /* Prevent any drag events on event wrapper */
                .rbc-event.rbc-selected {
                  pointer-events: none;
                }
                /* Fix popup positioning on mobile */
                .rbc-overlay {
                  max-width: 90vw;
                  max-height: 90vh;
                  overflow-y: auto;
                }
                @media (max-width: 767px) {
                  .rbc-overlay {
                    left: 50%;
                    transform: translateX(-50%);
                    position: fixed;
                  }
                }
                @media (min-width: 768px) {
                  .rbc-overlay {
                    left: auto;
                    transform: none;
                    position: absolute;
                  }
                }
              `}</style>
              <BigCalendar
                localizer={localizer}
                events={events}
                startAccessor="start"
                endAccessor="end"
                onSelectEvent={handleSelectEvent}
                onSelectSlot={handleSelectSlot}
                selectable
                eventPropGetter={eventStyleGetter}
                view="month"
                date={currentDate}
                onNavigate={handleNavigate}
                views={['month']}
                popup={true}
                draggableAccessor={() => false}
                resizableAccessor={() => false}
                onEventDrop={() => {}}
                onEventResize={() => {}}
                // Provide handleDragStart/handleDragEnd at BigCalendar level
                // React-big-calendar's internal code expects these to be available
                handleDragStart={NO_OP_DRAG_START}
                handleDragEnd={NO_OP_DRAG_END}
                culture="en"
                formats={{
                  monthHeaderFormat: 'MMMM YYYY',
                  dayHeaderFormat: 'dddd, MMMM Do',
                  dayFormat: 'D',
                  weekdayFormat: 'ddd'
                }}
                components={{
                  toolbar: CustomToolbar,
                  event: ({ event }) => <DraggableEvent event={event} onSelectEvent={handleSelectEvent} />,
                  eventWrapper: (wrapperProps) => {
                    // CRITICAL: Ensure handleDragStart/handleDragEnd are ALWAYS functions before passing to EventWrapper
                    // React-big-calendar's internal code accesses these from closure/props
                    // Create a new props object since wrapperProps is not extensible
                    const safeProps = {
                      ...wrapperProps,
                      handleDragStart: (wrapperProps.handleDragStart && typeof wrapperProps.handleDragStart === 'function') 
                        ? wrapperProps.handleDragStart 
                        : NO_OP_DRAG_START,
                      handleDragEnd: (wrapperProps.handleDragEnd && typeof wrapperProps.handleDragEnd === 'function') 
                        ? wrapperProps.handleDragEnd 
                        : NO_OP_DRAG_END
                    };
                    return <EventWrapper {...safeProps} />;
                  },
                  month: {
                    dateCellWrapper: DayComponent,
                    dayCellWrapper: DayComponent
                  }
                }}
              />
                </>
              )}
            </div>
          </div>
        </div>

        <DragOverlay style={{ pointerEvents: 'none' }}>
          {draggedItem ? (
            <div className="bg-white rounded-lg shadow-xl p-4 w-64 opacity-90" style={{ pointerEvents: 'none' }}>
              <div className="flex items-center gap-3">
                {(draggedItem.imageUrl || draggedItem.resource?.recipe?.imageUrl) ? (
                  <img 
                    src={getImageUrl(draggedItem.imageUrl || draggedItem.resource?.recipe?.imageUrl)} 
                    alt={draggedItem.title} 
                    className="w-16 h-16 object-cover rounded" 
                  />
                ) : (
                  <div className="w-16 h-16 bg-orange-100 rounded flex items-center justify-center flex-shrink-0">
                    <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
                <div>
                  <h3 className="font-semibold">{draggedItem.title}</h3>
                  <p className="text-xs text-gray-500">
                    {dragActiveData?.type === 'event' ? 'Moving meal...' : 'Drop on calendar day'}
                  </p>
                </div>
              </div>
            </div>
          ) : null}
        </DragOverlay>

        {/* Meal Type Selection Modal */}
        {showMealTypeModal && (pendingRecipe || pendingPantryItem) && pendingDate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-4 sm:p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-4">Plan Meal</h2>
              <div className="mb-4">
                <p className="text-lg font-semibold mb-2">{pendingRecipe?.title}</p>
                <p className="text-gray-600 mb-4">Date: {moment(pendingDate).format('MMMM DD, YYYY')}</p>
                {pendingPantryItem && (
                  <p className="text-sm text-sky-700 mb-2">
                    From freezer/pantry ({pendingPantryItem.portionsRemaining} portion(s) available)
                  </p>
                )}
              </div>
              <div className="mb-6">
                <p className="text-sm font-medium mb-3">Select Meal Type:</p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => handleMealTypeSelect('breakfast')}
                    className="px-6 py-3 bg-amber-400 hover:bg-amber-500 text-gray-900 rounded-lg transition-colors font-semibold shadow-md"
                  >
                    Breakfast
                  </button>
                  <button
                    onClick={() => handleMealTypeSelect('lunch')}
                    className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors font-semibold shadow-md"
                  >
                    Lunch
                  </button>
                  <button
                    onClick={() => handleMealTypeSelect('dinner')}
                    className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors font-semibold shadow-md"
                  >
                    Dinner
                  </button>
                  <button
                    onClick={() => handleMealTypeSelect('snack')}
                    className="px-6 py-3 bg-lime-500 hover:bg-lime-600 text-white rounded-lg transition-colors font-semibold shadow-md"
                  >
                    Snack
                  </button>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowMealTypeModal(false);
                  setPendingRecipe(null);
                  setPendingPantryItem(null);
                  setPendingDate(null);
                }}
                className="w-full px-6 py-3 bg-stone-600 text-white rounded-lg font-semibold hover:bg-stone-700 transition-colors shadow-md"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {showPantryScheduleModal && pendingPantryItem && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h2 className="text-xl font-bold mb-4">Schedule from pantry</h2>
              <p className="text-sm text-gray-600 mb-4">
                {pendingPantryItem.recipeId?.title} ({pendingPantryItem.portionsRemaining} portions)
              </p>
              <label className="block text-sm font-medium mb-2">Date</label>
              <input
                type="date"
                value={pantryScheduleDate}
                onChange={(e) => setPantryScheduleDate(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded mb-4"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={confirmPantrySchedule}
                  className="flex-1 px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700"
                >
                  Continue
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPantryScheduleModal(false);
                    setPendingPantryItem(null);
                    setPendingRecipe(null);
                  }}
                  className="flex-1 px-4 py-2 bg-stone-500 text-white rounded-lg hover:bg-stone-600"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Event Details Modal */}
        {showModal && selectedEvent && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-4 sm:p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-4">{selectedEvent.title}</h2>
              {selectedEvent.resource?.isFreezerDinner && (
                <p className="mb-3 text-sm text-gray-700 bg-blue-50 border border-dashed border-blue-400 rounded-lg px-3 py-2">
                  From freezer/pantry — reheat only; no new cooking needed for this meal.
                </p>
              )}
              {selectedEvent.resource?.isBatchCook && (
                <p className="mb-3 text-sm text-gray-700 bg-emerald-50 border border-emerald-400 rounded-lg px-3 py-2">
                  Batch cook — you will cook a larger batch; extra portions go to your freezer after saving.
                </p>
              )}
              {selectedEvent.resource?.isLeftover && !selectedEvent.resource?.isFreezerDinner && (
                <p className="mb-3 text-sm text-gray-700 bg-gray-100 border border-dashed border-gray-500 rounded-lg px-3 py-2">
                  Leftover serving — no new cooking needed for this meal.
                </p>
              )}
              <div className="mb-4">
                <p className="mb-2"><strong>Date:</strong> {moment(selectedEvent.start).format('MMMM DD, YYYY')}</p>
                <div className="mb-2">
                  <label className="block text-sm font-medium mb-2"><strong>Meal Type:</strong></label>
                  <select
                    value={selectedEvent.resource.mealType}
                    onChange={(e) => handleMealTypeChange(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    <option value="breakfast">Breakfast</option>
                    <option value="lunch">Lunch</option>
                    <option value="dinner">Dinner</option>
                    <option value="snack">Snack</option>
                  </select>
                </div>
              </div>
              {selectedEvent.resource.recipe.imageUrl ? (
                <img
                  src={getImageUrl(selectedEvent.resource.recipe.imageUrl)}
                  alt={selectedEvent.title}
                  className="w-full h-48 object-cover rounded mb-4"
                />
              ) : (
                <div className="w-full h-48 bg-orange-100 flex items-center justify-center rounded mb-4">
                  <div className="text-center text-gray-500">
                    <svg className="w-16 h-16 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-sm">No Image</p>
                  </div>
                </div>
              )}
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => {
                    setShowModal(false);
                    navigate(`/recipes/${selectedEvent.resource.recipeId}`);
                  }}
                  className="w-full px-6 py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-colors shadow-md"
                >
                  View Recipe Details
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={handleDeleteMealPlan}
                    className="flex-1 px-6 py-3 bg-rose-600 text-white rounded-lg font-semibold hover:bg-rose-700 transition-colors shadow-md"
                  >
                    Delete
                  </button>
                  <button
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-6 py-3 bg-stone-600 text-white rounded-lg font-semibold hover:bg-stone-700 transition-colors shadow-md"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {leftoverDeletePrompt && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-lg p-4 sm:p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-bold mb-3">Leftovers still on the calendar</h2>
              <p className="text-gray-700 mb-4">
                This meal still has leftover servings scheduled later on the calendar. Do you also want to remove them?
              </p>
              {leftoverDeletePrompt.leftoverMeals.length > 0 && (
                <ul className="mb-4 space-y-1 text-sm text-gray-600 bg-gray-50 rounded-lg p-3 border border-gray-200">
                  {leftoverDeletePrompt.leftoverMeals.map((meal) => (
                    <li key={meal.id}>
                      {meal.title} — {moment(meal.date).format('MMMM D, YYYY')}
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => handleLeftoverDeleteChoice(true)}
                  className="w-full px-6 py-3 bg-rose-600 text-white rounded-lg font-semibold hover:bg-rose-700 transition-colors shadow-md"
                >
                  Remove leftovers too
                </button>
                <button
                  onClick={() => handleLeftoverDeleteChoice(false)}
                  className="w-full px-6 py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-colors shadow-md"
                >
                  Keep leftovers on calendar
                </button>
                <button
                  onClick={() => setLeftoverDeletePrompt(null)}
                  className="w-full px-6 py-3 bg-stone-600 text-white rounded-lg font-semibold hover:bg-stone-700 transition-colors shadow-md"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Empty Calendar Modal */}
        {showEmptyCalendarModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h2 className="text-2xl font-bold mb-4">Empty Calendar</h2>
              <p className="text-gray-600 mb-6">
                Select a date range to delete all meal plans in that period.
              </p>
              
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={emptyStartDate}
                    onChange={(e) => setEmptyStartDate(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={emptyEndDate}
                    onChange={(e) => setEmptyEndDate(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={handleEmptyCalendar}
                  className="flex-1 px-6 py-3 bg-rose-600 text-white rounded-lg font-semibold hover:bg-rose-700 transition-colors shadow-md"
                >
                  Delete All
                </button>
                <button
                  onClick={() => {
                    setShowEmptyCalendarModal(false);
                    setEmptyStartDate('');
                    setEmptyEndDate('');
                  }}
                  className="flex-1 px-6 py-3 bg-stone-600 text-white rounded-lg font-semibold hover:bg-stone-700 transition-colors shadow-md"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </DndContext>
    </div>
    </>
  );
}

export default Calendar;

